// Error logs (Rerun queue).
//
// Lists every run with status=FAILED — the actionable failure state.
// Drilling into one shows the step timeline, the error message, and the
// screenshot from the failing step.
//
// Each row has a one-click "Rerun" button. The button POSTs to
// /runs/<id>/requeue, which:
//   1. Marks the source run's status FAILED -> RERUN ("handled" marker
//      so it disappears from this tab),
//   2. Creates a NEW RPARun (same workflow + params) with status=QUEUED.
//
// If the new run also fails, it lands as FAILED and shows up here for
// another retry — same cycle.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Stack, Typography, Button, IconButton, Chip, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Alert, CircularProgress, LinearProgress, Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LockIcon from '@mui/icons-material/Lock';
import CheckIcon from '@mui/icons-material/Check';
import { rpaRunList, rpaRunGet, rpaRunRequeue, rpaWorkflowList } from './api';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    StatusChip, RPA_COLORS, formatDateLong,
} from './rpaTheme';


export default function ErrorLogs() {
    const [runs, setRuns] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openRunId, setOpenRunId] = useState(null);
    const [requeueing, setRequeueing] = useState(null); // run id mid-flight

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [r, wf] = await Promise.all([
                // Only FAILED rows are actionable — RERUN rows are runs
                // the user has already retried and don't need attention.
                rpaRunList({ status: 'FAILED', per_page: 100 }),
                rpaWorkflowList({ per_page: 100 }),
            ]);
            if (r?.data?.success) setRuns(r.data.data || []);
            if (wf?.data?.success) setWorkflows(wf.data.data || []);
            else if (!r?.data?.success) setError(r?.data?.message);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const sessionExpired = runs.filter(
            (r) => (r.result_summary || {}).session_expired
        ).length;
        const policyViolations = runs.filter(
            (r) => /policy|read.?only|denied/i.test(r.error_message || '')
        ).length;
        return {
            total: runs.length,
            sessionExpired,
            policyViolations,
            other: runs.length - sessionExpired - policyViolations,
        };
    }, [runs]);

    const workflowName = (id) => {
        const w = workflows.find((x) => x.id === id);
        return w ? `${w.name} v${w.version}` : (id ? `#${id}` : '—');
    };

    const handleRequeue = async (runId, e) => {
        if (e) e.stopPropagation();
        setRequeueing(runId);
        setError(null);
        try {
            const r = await rpaRunRequeue(runId);
            if (!r?.data?.success) {
                throw new Error(r?.data?.message || 'Re-run failed');
            }
            const newRunId = r.data?.data?.id;
            window.alert(
                newRunId
                    ? `Re-queued as run #${newRunId}. The worker will pick it up on the next dispatch tick (≤30 s).`
                    : 'Re-run queued.'
            );
            // Refresh the list — the source run is now RERUN (handled)
            // and drops off this list; the new QUEUED run shows up in
            // the Runs tab.
            load();
        } catch (err) {
            setError(err?.response?.data?.message || err.message);
        } finally {
            setRequeueing(null);
        }
    };

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <KpiGrid>
                <KpiCard
                    label="Failed runs"
                    value={stats.total}
                    icon={<ErrorOutlineIcon fontSize="small" />}
                    accent={RPA_COLORS.danger}
                    hint="Click Rerun to retry"
                />
                <KpiCard
                    label="Session expired"
                    value={stats.sessionExpired}
                    icon={<VpnKeyIcon fontSize="small" />}
                    accent={RPA_COLORS.info}
                    hint="Re-record login leg or refresh creds"
                />
                <KpiCard
                    label="Policy violations"
                    value={stats.policyViolations}
                    icon={<LockIcon fontSize="small" />}
                    accent={RPA_COLORS.danger}
                    hint="Read-only deny-list tripped"
                />
                <KpiCard
                    label="Selector / other"
                    value={stats.other}
                    icon={<ErrorOutlineIcon fontSize="small" />}
                    accent={RPA_COLORS.neutral}
                />
            </KpiGrid>

            <SectionPaper>
                <Toolbar>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Failed runs — click Rerun to retry
                    </Typography>
                    <IconButton onClick={load} disabled={loading} size="small">
                        {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                </Toolbar>
                <TableShell>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Run</TableCell>
                                <TableCell>Merchant</TableCell>
                                <TableCell>Workflow</TableCell>
                                <TableCell>Error</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {runs.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                                        <EmptyState
                                            icon={<CheckIcon />}
                                            title="No failed runs"
                                            message="Nothing has failed recently. When a selector breaks or a session expires, the run lands here so you can rerun it."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {runs.map((r) => {
                                const summary = r.result_summary || {};
                                const reasonChip = summary.session_expired
                                    ? <Chip size="small" icon={<VpnKeyIcon sx={{ fontSize: 14 }} />}
                                            label="Session expired" color="info" />
                                    : /policy|read.?only/i.test(r.error_message || '')
                                    ? <Chip size="small" icon={<LockIcon sx={{ fontSize: 14 }} />}
                                            label="Policy violation" color="error" />
                                    : <Chip size="small" icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                            label="Selector / step failure" color="warning" />;
                                const isRequeueing = requeueing === r.id;
                                return (
                                    <TableRow
                                        key={r.id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => setOpenRunId(r.id)}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>#{r.id}</Typography>
                                            <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                                {formatDateLong(r.finished_at || r.started_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {r.merchant_name || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {r.workflow_name || workflowName(r.workflow_id)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{
                                                color: RPA_COLORS.textMuted,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}>
                                                {r.error_message || '—'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{reasonChip}</TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Rerun this failed run">
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={
                                                                isRequeueing
                                                                    ? <CircularProgress size={14} color="inherit" />
                                                                    : <ReplayIcon fontSize="small" />
                                                            }
                                                            onClick={(e) => handleRequeue(r.id, e)}
                                                            disabled={isRequeueing}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            {isRequeueing ? 'Queuing…' : 'Rerun'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="View failure details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setOpenRunId(r.id); }}
                                                    >
                                                        <OpenInFullIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableShell>
            </SectionPaper>

            <ErrorDetailDialog
                runId={openRunId}
                onClose={() => setOpenRunId(null)}
                onRequeue={handleRequeue}
                requeueing={requeueing}
            />
        </Box>
    );
}


function ErrorDetailDialog({ runId, onClose, onRequeue, requeueing }) {
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        if (runId == null) return;
        setLoading(true);
        setError(null);
        try {
            const r = await rpaRunGet(runId);
            if (r?.data?.success) setRun(r.data.data);
            else setError(r?.data?.message);
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [runId]);

    useEffect(() => {
        if (runId == null) { setRun(null); return; }
        load();
    }, [runId, load]);

    const sessionExpired = !!(run?.result_summary || {}).session_expired;
    const isRequeueing = requeueing === runId;

    return (
        <Dialog open={runId != null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <ErrorOutlineIcon sx={{ color: RPA_COLORS.danger }} />
                    <Typography variant="h6">Run #{runId} — failure details</Typography>
                    {run && <StatusChip status={run.status} />}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {(loading || !run) && <LinearProgress sx={{ mb: 2 }} />}

                {sessionExpired && (
                    <Alert severity="info" icon={<VpnKeyIcon />} sx={{ mb: 2 }}>
                        <strong>Session expired.</strong> The run landed back on the merchant's
                        login page mid-flow. Either re-record the login leg of the workflow,
                        or rotate the merchant's credentials before rerunning.
                    </Alert>
                )}

                {run?.error_message && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <strong>Run-level error:</strong> {run.error_message}
                    </Alert>
                )}

                {run && (
                    <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Step timeline
                        </Typography>
                        <Stack spacing={1}>
                            {(run.steps || []).length === 0 && (
                                <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                    No step rows — the run failed before any step was recorded.
                                </Typography>
                            )}
                            {(run.steps || []).map((s) => {
                                const broken = !!s.error;
                                return (
                                    <Box
                                        key={s.id}
                                        sx={{
                                            border: `1px solid ${broken ? RPA_COLORS.danger : RPA_COLORS.border}`,
                                            borderRadius: 1,
                                            p: 1.25,
                                            background: broken ? `${RPA_COLORS.danger}10` : 'transparent',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: broken ? 0.5 : 0 }}>
                                            <Chip
                                                size="small"
                                                label={`#${s.step_idx}`}
                                                sx={{ fontWeight: 700 }}
                                            />
                                            <Chip
                                                size="small"
                                                label={s.action || '—'}
                                                color={broken ? 'error' : 'default'}
                                                variant="outlined"
                                            />
                                            <Typography variant="caption" sx={{
                                                color: RPA_COLORS.textMuted,
                                                fontFamily: 'monospace',
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {s.selector_used || ''}
                                            </Typography>
                                            {s.ms_elapsed != null && (
                                                <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                                    {s.ms_elapsed} ms
                                                </Typography>
                                            )}
                                        </Stack>
                                        {broken && (
                                            <Alert severity="error" sx={{ mt: 0.5 }} icon={false}>
                                                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                                                    {s.error}
                                                </Typography>
                                            </Alert>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                        isRequeueing
                            ? <CircularProgress size={14} color="inherit" />
                            : <ReplayIcon fontSize="small" />
                    }
                    disabled={isRequeueing || !run}
                    onClick={() => { onRequeue(runId); }}
                    sx={{ textTransform: 'none' }}
                >
                    {isRequeueing ? 'Queuing…' : 'Rerun this run'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
